import React from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import { Wallet, ExternalLink, AlertTriangle, Clock, Download, Building2, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { EXACT_LOGO, ING_LOGO } from '../lib/logos';

const C1 = '#ff6200';
const C2 = '#ff8f00';

export default function SalarisPage() {
  return (
    <div className="tw1-fade-in" data-testid="salaris-page">
      <HeroBanner title="Salaris" subtitle="Handleiding salarisbetalingen via HR & Salaris Gemak + ING" variant="ing" icon={Wallet} logos={[{ src: EXACT_LOGO, alt: 'Exact' }, { src: ING_LOGO, alt: 'ING' }]} />

      <div className="space-y-5">
        {/* Sectie 1: SEPA downloaden */}
        <div className="tw1-card p-5" data-testid="salaris-download-steps">
          <div className="flex items-center gap-2 mb-4">
            <Download className="w-4 h-4" style={{ color: C1 }} />
            <h2 className="text-sm font-bold text-[#0b1220]">SEPA bestanden downloaden</h2>
          </div>
          <div className="space-y-3">
            {[
              { n: 1, text: 'Open HR & Salaris Gemak (wpps.nl/login)' },
              { n: 2, text: 'Download de SEPA-batches voor lonen' },
              { n: 3, text: 'Download de SEPA-batch voor loonbelasting' },
            ].map(s => (
              <div key={s.n} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold text-white" style={{ background: `linear-gradient(135deg, ${C1}, ${C2})` }}>{s.n}</div>
                <p className="text-sm text-[#3a4553] pt-1">{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Sectie 2: Saldo checken */}
        <div className="tw1-card p-5" data-testid="salaris-saldo-steps">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4" style={{ color: C1 }} />
            <h2 className="text-sm font-bold text-[#0b1220]">Saldo checken</h2>
          </div>
          <div className="space-y-3">
            {[
              { n: 4, text: 'Open ING Zakelijk (mijn.ing.nl/zakelijk)' },
              { n: 5, text: 'Check of er genoeg saldo staat op de betaalrekening' },
            ].map(s => (
              <div key={s.n} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold text-white" style={{ background: `linear-gradient(135deg, ${C1}, ${C2})` }}>{s.n}</div>
                <p className="text-sm text-[#3a4553] pt-1">{s.text}</p>
              </div>
            ))}

            {/* Tip bij stap 6 */}
            <div className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold text-white" style={{ background: `linear-gradient(135deg, ${C1}, ${C2})` }}>6</div>
              <p className="text-sm text-[#3a4553] pt-1">Te weinig? → Overboeken vanuit Group rekening met omschrijving <strong>"RC"</strong></p>
            </div>
          </div>
        </div>

        {/* Sectie 3: SEPA uploaden */}
        <div className="tw1-card p-5" data-testid="salaris-upload-steps">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4" style={{ color: C1 }} />
            <h2 className="text-sm font-bold text-[#0b1220]">SEPA uploaden bij ING</h2>
          </div>
          <div className="space-y-3">
            {[
              { n: 7, text: 'ING Zakelijk → Opdrachten → Upload verzamelbetalingen' },
              { n: 8, text: 'Upload het SEPA-bestand' },
              { n: 9, text: 'Controleer en klik op "Akkoord"' },
            ].map(s => (
              <div key={s.n} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold text-white" style={{ background: `linear-gradient(135deg, ${C1}, ${C2})` }}>{s.n}</div>
                <p className="text-sm text-[#3a4553] pt-1">{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Schema / Deadlines */}
        <div className="tw1-card p-5" data-testid="salaris-schema">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4" style={{ color: C1 }} />
            <h2 className="text-sm font-bold text-[#0b1220]">Schema & deadlines</h2>
          </div>
          <div className="space-y-2">
            <div className="rounded-lg p-3 border border-red-200 bg-red-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[#c0392b]" />
                <span className="text-sm font-semibold text-[#c0392b]">SEPA uploaden: woensdag vóór de laatste donderdag, VÓÓR 15:00</span>
              </div>
            </div>
            <div className="rounded-lg p-3 text-sm" style={{ background: `${C1}08` }}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: C1 }} />
                <span className="text-[#3a4553]">Loondag: <strong>laatste donderdag van de maand</strong></span>
              </div>
            </div>
            <div className="rounded-lg p-3 text-sm" style={{ background: `${C1}08` }}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: C1 }} />
                <span className="text-[#3a4553]">Loonbelasting vorige maand wordt mee-geïmporteerd met de SEPA's</span>
              </div>
            </div>
            <div className="rounded-lg p-3 text-sm" style={{ background: `${C1}08` }}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: C1 }} />
                <span className="text-[#3a4553]">Uiterlijk <strong>laatste dag van de maand</strong> bij de Belastingdienst</span>
              </div>
            </div>
          </div>
        </div>

        {/* Snelkoppelingen */}
        <div className="tw1-card p-5" data-testid="salaris-links">
          <h2 className="text-sm font-bold text-[#0b1220] mb-3">Snelkoppelingen</h2>
          <div className="flex flex-wrap gap-3">
            <a href="https://wpps.nl/login" target="_blank" rel="noopener noreferrer">
              <Button className="text-white text-sm" style={{ background: `linear-gradient(135deg, ${C1}, ${C2})` }} data-testid="salaris-wpps-link">
                <ExternalLink className="w-4 h-4 mr-2" /> Open HR & Salaris Gemak
              </Button>
            </a>
            <a href="https://mijn.ing.nl/zakelijk" target="_blank" rel="noopener noreferrer">
              <Button className="text-white text-sm" style={{ background: `linear-gradient(135deg, ${C1}, ${C2})` }} data-testid="salaris-ing-link">
                <ExternalLink className="w-4 h-4 mr-2" /> Open ING Zakelijk
              </Button>
            </a>
            <a href="https://start.exactonline.nl" target="_blank" rel="noopener noreferrer">
              <Button className="text-white text-sm" style={{ background: `linear-gradient(135deg, ${C1}, ${C2})` }} data-testid="salaris-exact-link">
                <ExternalLink className="w-4 h-4 mr-2" /> Open Exact Online
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
